import { ReactElement, useMemo } from 'react';
import { Stack, Typography } from '@mui/joy';
import CollapsibleSection from './CollapsibleSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import PageRow from './pagerow/PageRow';
import { Page, PageFilter, PageSortOrder } from '../../../db/pages';
import { StashTag } from '../../../db/constants';
import { filterPages, groupPagesByWindow, sortPages } from '../../sort';

import { InView } from 'react-intersection-observer';
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectSelectedUrls, addMultiToSelection, removeMultiFromSelection, selectSimilarUrls } from '../../../store/uiSlice';
import WindowGroupHeader from '../../components/WindowGroupHeader';
import { selectWindows, selectTabs } from '../../../store/windowsSlice';
import { useLiveQuery } from 'dexie-react-hooks';
import { getSimilarityScaler } from '../../../db/prefs';
import { useVirtualizer } from '@tanstack/react-virtual';

interface WindowGroupProps {
  windowId: number;
  pages: Page[];
  similarityScaler: number;
}

const WindowGroup = React.memo(({ windowId, pages, similarityScaler }: WindowGroupProps): ReactElement => {
  const windows = useAppSelector(selectWindows);
  const allTabs = useAppSelector(selectTabs);
  const currentWindow = windows[windowId];
  const windowTabs = currentWindow?.tabIds?.map(id => allTabs[id]).filter(Boolean) || undefined;
  const windowUrls = pages.map((p) => p.nurl.normalized);

  const isActiveWindow = Boolean(currentWindow) && !Number.isNaN(windowId);
  const allStashed = pages.every(p => p.humanTags && p.humanTags.includes(StashTag));

  const pageByUrl = useMemo(() => {
    const m = new Map<string, Page>();
    for (const p of pages) m.set(p.nurl.normalized, p);
    return m;
  }, [pages]);

  // Ordered tabIds for pages in this window group (browser tab-position order)
  const orderedTabIds = useMemo(() => {
    if (!isActiveWindow || !windowTabs) return [];
    return windowTabs
      .filter(t => t.id != null && t.url?.normalized && pageByUrl.has(t.url.normalized))
      .map(t => t.id!);
  }, [isActiveWindow, windowTabs, pageByUrl]);

  if (isActiveWindow && orderedTabIds.length > 0) {
    return (
      <InView>
        {({ inView, ref }) =>
          inView ? (
            <div
              ref={ref}
              style={{
                border: '1px solid',
                margin: '0.5rem 0.5rem 0.5rem 0.5rem',
                borderColor: 'rgb(50, 56, 62)',
              }}
            >
              <WindowGroupHeader
                urls={windowUrls}
                pageCount={pages.length}
                tabs={windowTabs}
                title={undefined}
                allStashed={allStashed}
              />
              <div>
                {orderedTabIds.map(tabId => {
                  const tab = allTabs[tabId];
                  const page = tab?.url ? pageByUrl.get(tab.url.normalized) : undefined;
                  if (!page) return <div key={tabId} style={{ height: '1.4rem' }} />;
                  return <PageRow key={tabId} page={page} windowId={windowId} tabId={tabId} similarityScaler={similarityScaler} />;
                })}
              </div>
            </div>
          ) : (
            <div ref={ref} style={{ height: pages.length * 25 }} />
          )
        }
      </InView>
    );
  }

  // Closed / archived window
  return (
    <InView>
      {({ inView, ref, entry }) =>
        inView ? (
          <div
            ref={ref}
            style={{
              border: '1px solid',
              margin: '0.5rem 0.5rem 0.5rem 0.5rem',
              borderColor: 'rgb(50, 56, 62)',
            }}
          >
            <WindowGroupHeader
                urls={windowUrls}
                pageCount={pages.length}
                tabs={windowTabs}
                title={Number.isNaN(windowId) ? "Archived" : undefined}
                allStashed={allStashed}
            />
            <div>
                {pages.map((page) => (
                <PageRow
                    page={page}
                    windowId={windowId}
                    key={page.nurl.normalized}
                    similarityScaler={similarityScaler}
                />
                ))}
            </div>
          </div>
        ) : (
          <div ref={ref} style={{ height: pages.length * 25 }} />
        )
      }
    </InView>
  );
});

interface RawPageListProps {
  pages: Page[];
  sortOrder: PageSortOrder;
  filter: PageFilter;
  groupByWindow: boolean;
  scrollEl?: HTMLDivElement | null;
  scrollMargin?: number;
}

const RawPageList = React.memo(({
  pages,
  sortOrder,
  filter,
  groupByWindow,
  scrollEl,
  scrollMargin = 0,
}: RawPageListProps): ReactElement => {
  const similarUrls = useAppSelector(selectSimilarUrls);
  // Single subscription for the entire list — avoids N per-PageRow Dexie subscriptions
  // (which caused stack overflows on the All tab with thousands of rows).
  const similarityScaler = useLiveQuery(getSimilarityScaler) ?? 5;

  // Pre-compute flat sorted pages for virtualizer count (always needed — hooks must be
  // called unconditionally).
  const sortedFlatPages = useMemo(() => {
    if (groupByWindow) return [];
    return sortPages(filterPages(pages, filter), sortOrder, similarUrls);
  }, [pages, sortOrder, filter, groupByWindow, similarUrls]);

  // Virtual scrolling for flat lists — only used when a scroll container is provided.
  // scrollMargin accounts for content above the list (e.g. tag sections in search results):
  // without it, virtual items appear offset when the user scrolls past the tag section.
  const virtualizer = useVirtualizer({
    count: sortedFlatPages.length,
    getScrollElement: () => scrollEl ?? null,
    estimateSize: () => 25,
    overscan: 10,
    scrollMargin,
  });

  // Memoize window-group rendering so it only re-runs when its actual inputs change.
  const windowGroups = useMemo(() => {
    if (!groupByWindow) return null;
    const windowGroupsSorted = groupPagesByWindow(filterPages(pages, filter));
    return windowGroupsSorted.map(([windowId, windowPages]) => (
      <WindowGroup
        key={windowId}
        windowId={Number(windowId)}
        pages={windowPages}
        similarityScaler={similarityScaler}
      />
    ));
  }, [pages, filter, groupByWindow, similarityScaler]);

  if (groupByWindow) {
    return <>{windowGroups}</>;
  }

  // Flat list with virtual scrolling when a scroll container is wired up.
  if (scrollEl) {
    const totalSize = virtualizer.getTotalSize();
    const virtualItems = virtualizer.getVirtualItems();
    return (
      <div style={{ height: totalSize, position: 'relative' }}>
        {virtualItems.map((virtualItem) => (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItem.start - virtualizer.options.scrollMargin}px)`,
            }}
          >
            <PageRow
              page={sortedFlatPages[virtualItem.index]}
              similarityScaler={similarityScaler}
              skipInView={true}
            />
          </div>
        ))}
      </div>
    );
  }

  // Fallback: InView-based rendering (no scroll container wired up).
  return (
    <>
      {sortedFlatPages.map((page) => (
        <PageRow page={page} key={page.nurl.normalized} similarityScaler={similarityScaler} />
      ))}
    </>
  );
});

export default RawPageList;
